// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from "react";

// Create context
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // ✅ loading flag

  // ✅ Load user & plan from localStorage
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      const storedPlan = localStorage.getItem("userPlan");

      if (storedUser) setUser(JSON.parse(storedUser));
      if (storedPlan) setUserPlan(JSON.parse(storedPlan));
    } catch (err) {
      console.error("Failed to parse user info or plan from localStorage:", err);
      localStorage.removeItem("userInfo");
      localStorage.removeItem("userPlan");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ✅ Login — save both user and optional plan
  const login = (userData, planData = null) => {
    try {
      setUser(userData);
      localStorage.setItem("userInfo", JSON.stringify(userData));

      if (planData) {
        setUserPlan(planData);
        localStorage.setItem("userPlan", JSON.stringify(planData));
      }
    } catch (error) {
      console.error("Error during login storage:", error);
    }
  };

  // ✅ Update plan independently
  const updateUserPlan = (planData) => {
    try {
      setUserPlan(planData);
      if (planData)
        localStorage.setItem("userPlan", JSON.stringify(planData));
      else localStorage.removeItem("userPlan");
    } catch (error) {
      console.error("Error updating user plan:", error);
    }
  };

  // ✅ Logout clears everything
  const logout = () => {
    try {
      setUser(null);
      setUserPlan(null);
      localStorage.removeItem("userInfo");
      localStorage.removeItem("userPlan");
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  // ✅ Helper: safely get current plan
  const getUserPlan = () => {
    if (userPlan) return userPlan;
    try {
      const storedPlan = localStorage.getItem("userPlan");
      return storedPlan ? JSON.parse(storedPlan) : null;
    } catch {
      return null;
    }
  };

  // ✅ Provide everything to the app
  const contextValue = {
    user,
    userPlan,
    setUserPlan: updateUserPlan,
    login,
    logout,
    getUserPlan,
    isLoading,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// ✅ Hook to use auth anywhere
export const useAuth = () => useContext(AuthContext);
